import { useState, useEffect, useRef, type CSSProperties } from 'react'
import { useI18n } from '../i18n/I18nContext'

interface LandingPageProps {
  onGetStarted: () => void
}

const FEATURE_COUNT = 8

const FEATURE_META = [
  { icon: '📋', color: '#4299e1' },
  { icon: '🔍', color: '#10b981' },
  { icon: '📖', color: '#8b5cf6' },
  { icon: '⭐', color: '#f59e0b' },
  { icon: '📊', color: '#ef4444' },
  { icon: '⚡', color: '#06b6d4' },
  { icon: '🔒', color: '#ec4899' },
  { icon: '📈', color: '#14b8a6' },
] as const

const STATS_META = [
  { value: '∞', icon: '📦', color: '#4299e1', labelKey: 'pages.landing.s0.label' },
  { value: '10+', icon: '🔍', color: '#10b981', labelKey: 'pages.landing.s1.label' },
  { value: '<100ms', icon: '⚡', color: '#f59e0b', labelKey: 'pages.landing.s2.label' },
  { value: '99.9%', icon: '✅', color: '#8b5cf6', labelKey: 'pages.landing.s3.label' },
] as const

const BENEFIT_META = [
  { icon: '⚡', labelKey: 'pages.landing.b0' },
  { icon: '🎯', labelKey: 'pages.landing.b1' },
  { icon: '📊', labelKey: 'pages.landing.b2' },
  { icon: '🔒', labelKey: 'pages.landing.b3' },
  { icon: '🚀', labelKey: 'pages.landing.b4' },
  { icon: '💡', labelKey: 'pages.landing.b5' },
] as const

const TESTIMONIAL_INDEX = [0, 1, 2] as const

const COMPARE_ROWS = ['readme', 'filters', 'favorites', 'stats', 'performance', 'ui'] as const

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const { t, locale, setLocale } = useI18n()
  const [theme, setTheme] = useState<'light' | 'dark'>(
    (localStorage.getItem('theme') as 'light' | 'dark') || 'dark'
  )
  const [activeFeature, setActiveFeature] = useState(0)
  const heroRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.body.dataset.theme = theme
    localStorage.setItem('theme', theme)
  }, [theme])

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % FEATURE_COUNT)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="landing-page">
      <div className="landing-background">
        <div className="gradient-orb orb-1" style={{ '--delay': '0s' } as CSSProperties}></div>
        <div className="gradient-orb orb-2" style={{ '--delay': '2s' } as CSSProperties}></div>
        <div className="gradient-orb orb-3" style={{ '--delay': '4s' } as CSSProperties}></div>
      </div>

      <header className="landing-header">
        <div className="landing-logo">
          <div className="logo-icon">
            <span className="logo-emoji">📦</span>
            <div className="logo-pulse"></div>
          </div>
          <div>
            <h1>{t('nav.brand')}</h1>
            <p className="logo-subtitle">{t('pages.landing.logoSubtitle')}</p>
          </div>
        </div>
        <div className="header-actions landing-header-actions">
          <div className="locale-switch" role="group" aria-label={t('nav.aria.language')}>
            <button
              type="button"
              className={`btn-secondary small${locale === 'fr' ? ' active' : ''}`}
              onClick={() => setLocale('fr')}
              aria-pressed={locale === 'fr'}
            >
              {t('nav.locale.fr')}
            </button>
            <button
              type="button"
              className={`btn-secondary small${locale === 'en' ? ' active' : ''}`}
              onClick={() => setLocale('en')}
              aria-pressed={locale === 'en'}
            >
              {t('nav.locale.en')}
            </button>
          </div>
          <button
            type="button"
            className="theme-toggle-btn"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={t('nav.themeToggle')}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </header>

      <main className="landing-main">
        <section className="hero-section" ref={heroRef}>
          <div className="hero-content">
            <div className="hero-badges">
              <span className="badge pulse">{t('pages.landing.badgeNew')}</span>
              <span className="badge">{t('pages.landing.badgeFast')}</span>
              <span className="badge">{t('pages.landing.badgeSecure')}</span>
            </div>
            <h1 className="hero-title">
              {t('pages.landing.heroTitleA')}
              <span className="gradient-text animate-gradient">{t('pages.landing.heroTitleB')}</span>
            </h1>
            <p className="hero-description">
              {t('pages.landing.heroDesc')}
            </p>
            <div className="hero-actions">
              <button type="button" className="btn-primary large" onClick={onGetStarted}>
                <span>{t('pages.landing.ctaLong')}</span>
                <span className="btn-arrow">→</span>
              </button>
            </div>

            <div className="hero-stats">
              {STATS_META.map((stat, index) => (
                <div
                  key={index}
                  className="stat-item"
                  style={{ '--stat-color': stat.color } as CSSProperties}
                >
                  <span className="stat-icon">{stat.icon}</span>
                  <div>
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{t(stat.labelKey)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="hero-benefits">
              {BENEFIT_META.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <span className="benefit-icon">{benefit.icon}</span>
                  <span className="benefit-text">{t(benefit.labelKey)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-visual">
            <div className="code-preview floating">
              <div className="code-header">
                <div className="code-dots">
                  <span className="code-dot"></span>
                  <span className="code-dot"></span>
                  <span className="code-dot"></span>
                </div>
                <span className="code-title">{t('pages.landing.codeTitle')}</span>
              </div>
              <pre className="code-content">
                <code>{`POST /graphql
query {
  repos(page: 1, perPage: 24,
        sort: STARS, language: "Python") {
    total
    items { fullName stargazersCount }
  }
}`}</code>
              </pre>
            </div>
            <div className="feature-preview">
              <div className="preview-card">
                <div className="preview-icon">📖</div>
                <div className="preview-text">{t('pages.landing.preview.readme')}</div>
              </div>
              <div className="preview-card">
                <div className="preview-icon">⭐</div>
                <div className="preview-text">{t('pages.landing.preview.favs')}</div>
              </div>
              <div className="preview-card">
                <div className="preview-icon">📊</div>
                <div className="preview-text">{t('pages.landing.preview.stats')}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="features-section">
          <div className="section-header">
            <h2 className="section-title">{t('pages.landing.section.featuresTitle')}</h2>
            <p className="section-subtitle">
              {t('pages.landing.section.featuresSubtitle')}
            </p>
          </div>
          <div className="features-grid">
            {FEATURE_META.map((feature, index) => (
              <div
                key={index}
                className={`feature-card ${index === activeFeature ? 'active' : ''}`}
                style={{ '--feature-color': feature.color } as CSSProperties}
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="feature-icon-wrapper">
                  <div className="feature-icon">{feature.icon}</div>
                  <div className="feature-glow"></div>
                </div>
                <h3 className="feature-title">{t(`pages.landing.f${index}.title`)}</h3>
                <p className="feature-description">{t(`pages.landing.f${index}.desc`)}</p>
                <div className="feature-link">
                  {t('pages.landing.feature.learnMore')}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="testimonials-section">
          <div className="section-header">
            <h2 className="section-title">{t('pages.landing.section.testimonialsTitle')}</h2>
            <p className="section-subtitle">
              {t('pages.landing.section.testimonialsSubtitle')}
            </p>
          </div>
          <div className="testimonials-grid">
            {TESTIMONIAL_INDEX.map((i) => (
              <div key={i} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="testimonial-avatar">{['👨‍💻', '👩‍💼', '👨‍🔬'][i]}</div>
                  <div className="testimonial-info">
                    <div className="testimonial-name">{t(`pages.landing.t${i}.name`)}</div>
                    <div className="testimonial-role">{t(`pages.landing.t${i}.role`)}</div>
                  </div>
                </div>
                <div className="testimonial-rating">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <span key={j} className="star">⭐</span>
                  ))}
                </div>
                <p className="testimonial-text">&quot;{t(`pages.landing.t${i}.text`)}&quot;</p>
              </div>
            ))}
          </div>
        </section>

        <section className="how-it-works">
          <div className="section-header">
            <h2 className="section-title">{t('pages.landing.section.howTitle')}</h2>
            <p className="section-subtitle">{t('pages.landing.section.howSubtitle')}</p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>{t('pages.landing.step1.title')}</h3>
              <p>{t('pages.landing.step1.desc')}</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>{t('pages.landing.step2.title')}</h3>
              <p>{t('pages.landing.step2.desc')}</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>{t('pages.landing.step3.title')}</h3>
              <p>{t('pages.landing.step3.desc')}</p>
            </div>
          </div>
        </section>

        <section className="comparison-section">
          <div className="section-header">
            <h2 className="section-title">{t('pages.landing.section.compareTitle')}</h2>
            <p className="section-subtitle">
              {t('pages.landing.section.compareSubtitle')}
            </p>
          </div>
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell feature-col">{t('pages.landing.compare.header.feature')}</div>
              <div className="comparison-cell">{t('pages.landing.compare.header.classic')}</div>
              <div className="comparison-cell highlight">{t('pages.landing.compare.header.platform')}</div>
            </div>
            {COMPARE_ROWS.map((row) => (
              <div key={row} className="comparison-row">
                <div className="comparison-cell feature-col">{t(`pages.landing.compare.${row}.label`)}</div>
                <div className="comparison-cell">{t(`pages.landing.compare.${row}.classic`)}</div>
                <div className="comparison-cell highlight">{t(`pages.landing.compare.${row}.platform`)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-content">
            <div className="cta-icon">🚀</div>
            <h2>{t('pages.landing.cta.title')}</h2>
            <p>{t('pages.landing.cta.subtitle')}</p>
            <div className="cta-actions">
              <button type="button" className="btn-primary large" onClick={onGetStarted}>
                <span>{t('pages.landing.cta.button')}</span>
                <span className="btn-arrow">→</span>
              </button>
            </div>
            <div className="cta-trust">
              <span>{t('pages.landing.cta.trust1')}</span>
              <span>{t('pages.landing.cta.trust2')}</span>
              <span>{t('pages.landing.cta.trust3')}</span>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t('pages.landing.footer.brand')}</h4>
            <p>{t('pages.landing.footer.tagline')}</p>
          </div>
          <div className="footer-section">
            <h4>{t('pages.landing.footer.techTitle')}</h4>
            <p>{t('pages.landing.footer.techLine1')}</p>
            <p>{t('pages.landing.footer.techLine2')}</p>
          </div>
          <div className="footer-section">
            <h4>{t('pages.landing.footer.featTitle')}</h4>
            <p>{t('pages.landing.footer.featLine1')}</p>
            <p>{t('pages.landing.footer.featLine2')}</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('pages.landing.footer.copy')}</p>
        </div>
      </footer>
    </div>
  )
}
